/**
 * Derived topology for one floor plan (pure, deterministic):
 *
 *  - planar-graph FACE tracing over shared nodes/walls → enclosed REGIONS
 *    (centerline loop, interior polygon offset by half wall thickness,
 *    exact area sqft×100 and perimeter ft×100);
 *  - ROOM binding: each RoomAnchor belongs to the region containing it;
 *    conflicts (two rooms in one region) and unbound rooms are reported,
 *    never auto-merged or deleted;
 *  - WALL RUNS: maximal collinear chains through degree-2 nodes with equal
 *    thickness (the user experiences one coherent wall);
 *  - EXTERIOR classification: a wall bordering an outer face is exterior
 *    unless overridden;
 *  - GAP detection: dangling wall ends near another node/wall.
 *
 * Nothing here is persisted as truth; room_geometry caches the results.
 */
import type { PlanModel, PlanNode, Point, Wall } from "./model";
import { activeNodes, activeWalls } from "./model";
import {
  collinear,
  closestPointOnSegment,
  distance,
  leftNormal,
  lineIntersection,
  perimeterLength,
  pointInPolygon,
  roundPoint,
  sub,
  twiceSignedArea,
} from "./vector";
import { PU_PER_INCH, puToFeetX100, sqPuToSqftX100 } from "./units";
import { interiorPoint } from "./roomBinding";

export interface PlanRegion {
  /** Stable derived key: sorted wall ids (changes only when the boundary changes). */
  key: string;
  nodeIds: string[];
  wallIds: string[];
  /** Centerline loop (spurs removed), CCW. */
  centerline: Point[];
  /** Interior polygon (faces offset inward by half thickness), CCW. */
  interior: Point[];
  floorAreaX100: number;
  perimeterX100: number;
  roomIds: string[];
  status: "unassigned" | "bound" | "conflict";
  /** Centroid of the interior polygon (label placement / default anchor). */
  centroid: Point;
  /**
   * A point GUARANTEED to be inside this region — where the room name and
   * area are drawn, and where a new room anchor is placed. A centroid is
   * outside an L-shaped or U-shaped room, which is how labels used to end
   * up in the room next door.
   */
  labelPoint: Point;
}

export interface WallRun {
  id: string;
  wallIds: string[];
  startNodeId: string;
  endNodeId: string;
  lengthPu: number;
}

export interface PlanGap {
  nodeId: string;
  targetNodeId: string | null;
  targetWallId: string | null;
  /** Closest point on the target (where "Close gap" would move the node). */
  targetPoint: Point;
  distancePu: number;
}

export interface PlanAnalysis {
  regions: PlanRegion[];
  runs: WallRun[];
  runByWall: ReadonlyMap<string, WallRun>;
  exteriorWallIds: ReadonlySet<string>;
  gaps: PlanGap[];
  /** roomId → region key for bound/conflicting rooms. */
  roomRegion: ReadonlyMap<string, string>;
  unboundRoomIds: string[];
  conflictRegionKeys: string[];
}

/** Dangling ends closer than this to other geometry are reported as gaps (3"). */
export const GAP_TOLERANCE_PU = 3 * PU_PER_INCH;

interface HalfEdge {
  from: string;
  to: string;
  wallId: string;
}

interface Face {
  entries: Array<{ nodeId: string; wallId: string }>; // wallId = wall to next entry
  twiceArea: number;
}

function adjacency(nodes: PlanNode[], walls: Wall[]): Map<string, Array<{ to: string; wallId: string; angle: number }>> {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const adj = new Map<string, Array<{ to: string; wallId: string; angle: number }>>();
  for (const n of nodes) adj.set(n.id, []);
  for (const w of walls) {
    const a = byId.get(w.startNodeId);
    const b = byId.get(w.endNodeId);
    if (!a || !b || a.id === b.id) continue;
    adj.get(a.id)!.push({ to: b.id, wallId: w.id, angle: Math.atan2(b.y - a.y, b.x - a.x) });
    adj.get(b.id)!.push({ to: a.id, wallId: w.id, angle: Math.atan2(a.y - b.y, a.x - b.x) });
  }
  for (const list of adj.values()) list.sort((p, q) => p.angle - q.angle || p.wallId.localeCompare(q.wallId));
  return adj;
}

/** Trace all faces; inner faces have positive twiceArea (CCW), outer faces negative. */
function traceFaces(model: PlanModel): { faces: Face[]; wallOuter: Set<string> } {
  const nodes = activeNodes(model);
  const walls = activeWalls(model);
  const adj = adjacency(nodes, walls);
  const nodeById = new Map(nodes.map((n) => [n.id, n]));
  const visited = new Set<string>();
  const faces: Face[] = [];
  const wallOuter = new Set<string>();
  const key = (h: HalfEdge) => `${h.from}|${h.to}|${h.wallId}`;
  const halfEdges: HalfEdge[] = [];
  for (const w of walls) {
    if (!nodeById.has(w.startNodeId) || !nodeById.has(w.endNodeId)) continue;
    halfEdges.push({ from: w.startNodeId, to: w.endNodeId, wallId: w.id });
    halfEdges.push({ from: w.endNodeId, to: w.startNodeId, wallId: w.id });
  }
  halfEdges.sort((a, b) => key(a).localeCompare(key(b)));
  for (const start of halfEdges) {
    if (visited.has(key(start))) continue;
    const entries: Array<{ nodeId: string; wallId: string }> = [];
    const faceWalls: string[] = [];
    let current = start;
    let guard = 0;
    while (!visited.has(key(current)) && guard++ < 100000) {
      visited.add(key(current));
      entries.push({ nodeId: current.from, wallId: current.wallId });
      faceWalls.push(current.wallId);
      const list = adj.get(current.to)!;
      const backIndex = list.findIndex((e) => e.to === current.from && e.wallId === current.wallId);
      const next = list[(backIndex - 1 + list.length) % list.length];
      current = { from: current.to, to: next.to, wallId: next.wallId };
    }
    const pts = entries.map((e) => nodeById.get(e.nodeId)!);
    const twiceArea = twiceSignedArea(pts);
    faces.push({ entries, twiceArea });
    if (twiceArea <= 0) for (const wId of faceWalls) wallOuter.add(wId);
  }
  return { faces, wallOuter };
}

/** Remove spurs (X,Y,X → X) from a cyclic entry list; returns a simple loop. */
function removeSpurs(entries: Array<{ nodeId: string; wallId: string }>): Array<{ nodeId: string; wallId: string }> {
  let arr = entries.slice();
  let changed = true;
  while (changed && arr.length >= 3) {
    changed = false;
    const n = arr.length;
    for (let i = 0; i < n; i++) {
      const prev = arr[(i - 1 + n) % n];
      const next = arr[(i + 1) % n];
      if (prev.nodeId === next.nodeId) {
        // remove entries (i-1) and i
        const remove = new Set([(i - 1 + n) % n, i]);
        arr = arr.filter((_, idx) => !remove.has(idx));
        changed = true;
        break;
      }
    }
  }
  return arr;
}

function interiorPolygon(loop: Point[], halfThickness: number[]): Point[] {
  const n = loop.length;
  if (n < 3) return loop.slice();
  const out: Point[] = [];
  for (let i = 0; i < n; i++) {
    const pPrev = loop[(i - 1 + n) % n];
    const p = loop[i];
    const pNext = loop[(i + 1) % n];
    const dPrev = halfThickness[(i - 1 + n) % n];
    const dNext = halfThickness[i];
    const nPrev = leftNormal(pPrev, p);
    const nNext = leftNormal(p, pNext);
    const a1 = { x: pPrev.x + nPrev.x * dPrev, y: pPrev.y + nPrev.y * dPrev };
    const a2 = { x: p.x + nPrev.x * dPrev, y: p.y + nPrev.y * dPrev };
    const b1 = { x: p.x + nNext.x * dNext, y: p.y + nNext.y * dNext };
    const b2 = { x: pNext.x + nNext.x * dNext, y: pNext.y + nNext.y * dNext };
    const dirPrev = sub(p, pPrev);
    const dirNext = sub(pNext, p);
    let v: { x: number; y: number } | null = null;
    if (!collinear(dirPrev, dirNext, 0.02)) v = lineIntersection(a1, a2, b1, b2);
    if (!v) v = { x: p.x + ((nPrev.x + nNext.x) / 2) * ((dPrev + dNext) / 2), y: p.y + ((nPrev.y + nNext.y) / 2) * ((dPrev + dNext) / 2) };
    out.push(roundPoint(v));
  }
  return out;
}

function centroid(points: Point[]): Point {
  if (points.length === 0) return { x: 0, y: 0 };
  let sx = 0;
  let sy = 0;
  for (const p of points) {
    sx += p.x;
    sy += p.y;
  }
  return roundPoint({ x: sx / points.length, y: sy / points.length });
}

function computeRuns(model: PlanModel): { runs: WallRun[]; runByWall: Map<string, WallRun> } {
  const walls = activeWalls(model).filter((w) => w.kind !== "boundary");
  const nodeById = new Map(activeNodes(model).map((n) => [n.id, n]));
  const wallsAtNode = new Map<string, Wall[]>();
  for (const w of walls) {
    for (const nid of [w.startNodeId, w.endNodeId]) {
      if (!wallsAtNode.has(nid)) wallsAtNode.set(nid, []);
      wallsAtNode.get(nid)!.push(w);
    }
  }
  const dirOf = (w: Wall) => {
    const a = nodeById.get(w.startNodeId)!;
    const b = nodeById.get(w.endNodeId)!;
    return sub(b, a);
  };
  /** The wall continuing `w` through node `nid`, or null. */
  const continuation = (w: Wall, nid: string): Wall | null => {
    // T-junctions do not break a run: continue into the UNIQUE collinear
    // wall of equal thickness at this node (any degree).
    const list = (wallsAtNode.get(nid) ?? []).filter(
      (o) => o.id !== w.id && o.thickness === w.thickness && nodeById.has(o.startNodeId) && nodeById.has(o.endNodeId) && collinear(dirOf(w), dirOf(o)),
    );
    return list.length === 1 ? list[0] : null;
  };
  const assigned = new Set<string>();
  const runs: WallRun[] = [];
  const runByWall = new Map<string, WallRun>();
  const sorted = walls.slice().sort((a, b) => a.id.localeCompare(b.id));
  for (const seed of sorted) {
    if (assigned.has(seed.id) || !nodeById.has(seed.startNodeId) || !nodeById.has(seed.endNodeId)) continue;
    // walk backwards from seed to find the run start
    let first = seed;
    let firstNode = seed.startNodeId;
    const seen = new Set<string>([seed.id]);
    for (;;) {
      const prev = continuation(first, firstNode);
      if (!prev || seen.has(prev.id)) break;
      seen.add(prev.id);
      firstNode = prev.startNodeId === firstNode ? prev.endNodeId : prev.startNodeId;
      first = prev;
    }
    // walk forward from first
    const wallIds: string[] = [];
    let current = first;
    let entryNode = firstNode;
    let lengthPu = 0;
    const walked = new Set<string>();
    for (;;) {
      wallIds.push(current.id);
      walked.add(current.id);
      assigned.add(current.id);
      const a = nodeById.get(current.startNodeId)!;
      const b = nodeById.get(current.endNodeId)!;
      lengthPu += Math.round(distance(a, b));
      const exitNode = current.startNodeId === entryNode ? current.endNodeId : current.startNodeId;
      const next = continuation(current, exitNode);
      if (!next || walked.has(next.id)) {
        const run: WallRun = { id: `run:${first.id}`, wallIds, startNodeId: firstNode, endNodeId: exitNode, lengthPu };
        runs.push(run);
        for (const id of wallIds) runByWall.set(id, run);
        break;
      }
      entryNode = exitNode;
      current = next;
    }
  }
  return { runs, runByWall };
}

function computeGaps(model: PlanModel): PlanGap[] {
  const walls = activeWalls(model);
  const nodes = activeNodes(model);
  const nodeById = new Map(nodes.map((n) => [n.id, n]));
  const degree = new Map<string, number>();
  for (const w of walls) {
    degree.set(w.startNodeId, (degree.get(w.startNodeId) ?? 0) + 1);
    degree.set(w.endNodeId, (degree.get(w.endNodeId) ?? 0) + 1);
  }
  const gaps: PlanGap[] = [];
  for (const n of nodes) {
    if ((degree.get(n.id) ?? 0) !== 1) continue;
    const myWall = walls.find((w) => w.startNodeId === n.id || w.endNodeId === n.id)!;
    let best: PlanGap | null = null;
    for (const other of nodes) {
      if (other.id === n.id || other.id === myWall.startNodeId || other.id === myWall.endNodeId) continue;
      const d = distance(n, other);
      if (d > 0 && d <= GAP_TOLERANCE_PU && (!best || d < best.distancePu)) {
        best = { nodeId: n.id, targetNodeId: other.id, targetWallId: null, targetPoint: { x: other.x, y: other.y }, distancePu: d };
      }
    }
    if (!best) {
      for (const w of walls) {
        if (w.id === myWall.id) continue;
        const a = nodeById.get(w.startNodeId);
        const b = nodeById.get(w.endNodeId);
        if (!a || !b) continue;
        const c = closestPointOnSegment(n, a, b);
        if (c.distance > 0 && c.distance <= GAP_TOLERANCE_PU && (!best || c.distance < best.distancePu)) {
          best = { nodeId: n.id, targetNodeId: null, targetWallId: w.id, targetPoint: c.point, distancePu: c.distance };
        }
      }
    }
    if (best) gaps.push(best);
  }
  return gaps;
}

export function analyzePlan(model: PlanModel): PlanAnalysis {
  const nodeById = new Map(activeNodes(model).map((n) => [n.id, n]));
  const { faces, wallOuter } = traceFaces(model);
  const regions: PlanRegion[] = [];
  for (const face of faces) {
    if (face.twiceArea <= 0) continue;
    const loopEntries = removeSpurs(face.entries);
    if (loopEntries.length < 3) continue;
    const loop = loopEntries.map((e) => nodeById.get(e.nodeId)!);
    if (twiceSignedArea(loop) <= 0) continue;
    const half = loopEntries.map((e) => (model.walls.get(e.wallId)?.thickness ?? 0) / 2);
    const interior = interiorPolygon(loop, half);
    const interiorTwice = twiceSignedArea(interior);
    const wallIds = [...new Set(face.entries.map((e) => e.wallId))].sort();
    regions.push({
      key: `region:${wallIds.join(",")}`,
      nodeIds: loopEntries.map((e) => e.nodeId),
      wallIds,
      centerline: loop,
      interior,
      floorAreaX100: interiorTwice > 0 ? sqPuToSqftX100(interiorTwice / 2) : 0,
      perimeterX100: interiorTwice > 0 ? puToFeetX100(perimeterLength(interior)) : 0,
      roomIds: [],
      status: "unassigned",
      centroid: centroid(interiorTwice > 0 ? interior : loop),
      labelPoint: interiorPoint(interior.length >= 3 ? interior : loop),
    });
  }
  // Room anchors → regions (smallest containing region wins for nested components).
  const roomRegion = new Map<string, string>();
  const unboundRoomIds: string[] = [];
  for (const anchor of [...model.anchors.values()].sort((a, b) => a.roomId.localeCompare(b.roomId))) {
    let best: PlanRegion | null = null;
    for (const r of regions) {
      if (pointInPolygon(anchor, r.centerline) && (!best || r.floorAreaX100 < best.floorAreaX100)) best = r;
    }
    if (best) {
      best.roomIds.push(anchor.roomId);
      roomRegion.set(anchor.roomId, best.key);
    } else {
      unboundRoomIds.push(anchor.roomId);
    }
  }
  const conflictRegionKeys: string[] = [];
  for (const r of regions) {
    r.status = r.roomIds.length === 0 ? "unassigned" : r.roomIds.length === 1 ? "bound" : "conflict";
    if (r.status === "conflict") conflictRegionKeys.push(r.key);
  }
  const exteriorWallIds = new Set<string>();
  for (const w of activeWalls(model)) {
    if (w.kind === "boundary") continue; // logical boundaries are never building envelope
    const exterior = w.exteriorOverride ?? wallOuter.has(w.id);
    if (exterior) exteriorWallIds.add(w.id);
  }
  const { runs, runByWall } = computeRuns(model);
  return { regions, runs, runByWall, exteriorWallIds, gaps: computeGaps(model), roomRegion, unboundRoomIds, conflictRegionKeys };
}

/** Region containing a point (smallest area wins), or null. */
export function regionAt(analysis: PlanAnalysis, p: Point): PlanRegion | null {
  let best: PlanRegion | null = null;
  for (const r of analysis.regions) {
    if (pointInPolygon(p, r.centerline) && (!best || r.floorAreaX100 < best.floorAreaX100)) best = r;
  }
  return best;
}
